import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, ShoppingCart, Wrench, Package, Calculator, FileDown, Users, Recycle, Smartphone } from "lucide-react";
import DashboardShell, { StatCard, StatusBadge } from "../components/DashboardShell";
import PushToggle from "../components/mobile/PushToggle";
import ClientsPanel from "../components/dashboard/ClientsPanel";
import KegsPanel from "../components/dashboard/KegsPanel";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AgentDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [machines, setMachines] = useState([]);
  const [orders, setOrders] = useState([]);
  const [comodati, setComodati] = useState([]);
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState({ rates: {}, new_point_bonus: 0 });

  // order builder
  const [orderOpen, setOrderOpen] = useState(false);
  const [client, setClient] = useState({ client_name: "", client_type: "", client_city: "", notes: "", new_point: false });
  const [cart, setCart] = useState([]);

  // comodato
  const [comOpen, setComOpen] = useState(false);
  const [com, setCom] = useState({ client_name: "", machine_id: "", lease_months: 12, monthly_kegs: 10, margin_per_keg: 20, notes: "" });

  const load = async () => {
    const [s, p, m, o, c, cl, cs] = await Promise.all([
      api.get("/stats"), api.get("/products"), api.get("/machines"), api.get("/orders"), api.get("/comodati"),
      api.get("/clients"), api.get("/settings/commission"),
    ]);
    setStats(s.data); setProducts(p.data); setMachines(m.data); setOrders(o.data); setComodati(c.data);
    setClients(cl.data); setSettings(cs.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const dl = async (url, fname) => {
    try { await downloadFile(url, fname); } catch { toast.error("Download non riuscito"); }
  };

  const baseRate = user?.commission_rate ?? 8;
  const discount = Number(user?.discount_pct ?? 0);
  const rateFor = (cat) => Number(settings.rates?.[cat] ?? baseRate);
  const effPrice = (p) => Math.round((p.agent_price || 0) * (1 - discount / 100) * 100) / 100;
  const addToCart = (p) => {
    if (cart.find((i) => i.product_id === p.id)) return;
    setCart([...cart, { product_id: p.id, name: p.name, quantity: 1, unit_price: effPrice(p), category: p.category, portions: p.portions }]);
  };
  const updateItem = (id, field, val) => setCart(cart.map((i) => i.product_id === id ? { ...i, [field]: val } : i));
  const removeItem = (id) => setCart(cart.filter((i) => i.product_id !== id));
  const cartTotal = cart.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const cartVat = cartTotal * 0.22;
  const cartGross = cartTotal + cartVat;
  let cartCommission = cart.reduce((s, i) => s + i.quantity * i.unit_price * rateFor(i.category) / 100, 0);
  if (client.new_point) cartCommission += cartTotal * Number(settings.new_point_bonus ?? 0) / 100;
  const effRate = cartTotal ? (cartCommission / cartTotal * 100) : 0;

  const submitOrder = async () => {
    if (!client.client_name || cart.length === 0) { toast.error("Inserisci cliente e almeno un prodotto"); return; }
    try {
      await api.post("/orders", { ...client, items: cart.map((i) => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })) });
      toast.success("Ordine creato con successo");
      setOrderOpen(false); setCart([]); setClient({ client_name: "", client_type: "", client_city: "", notes: "", new_point: false });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const selectedMachine = machines.find((m) => m.id === com.machine_id);
  const comAbsorption = com.monthly_kegs * com.margin_per_keg;
  const comBreakeven = selectedMachine && comAbsorption > 0 ? Math.ceil(selectedMachine.cost / comAbsorption) : null;

  const submitComodato = async () => {
    if (!com.client_name || !com.machine_id) { toast.error("Inserisci cliente e macchina"); return; }
    try {
      await api.post("/comodati", {
        ...com, lease_months: Number(com.lease_months), monthly_kegs: Number(com.monthly_kegs), margin_per_keg: Number(com.margin_per_keg),
      });
      toast.success("Richiesta comodato inviata");
      setComOpen(false); setCom({ client_name: "", machine_id: "", lease_months: 12, monthly_kegs: 10, margin_per_keg: 20, notes: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <DashboardShell title={`Ciao, ${user?.name?.split(" ")[0] || "Agente"}`} subtitle={`Provvigioni a scaglioni per categoria · Zona: ${user?.zone || "—"}`}>
      <div className="flex items-center justify-end gap-2 -mt-4 mb-4 text-sm text-stone-500" data-testid="agent-push-row"><a href="/agente-mobile" className="inline-flex items-center gap-1 rounded-full border border-[#0F4C81] text-[#0F4C81] px-3 h-8 text-xs font-semibold mr-auto" data-testid="agent-open-mobile-btn"><Smartphone className="w-3.5 h-3.5" />Apri versione mobile</a><span>Notifiche ordini clienti</span><PushToggle /></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Ordini totali" value={stats.orders_count ?? 0} testid="stat-orders" />
        <StatCard label="Venduto" value={eur(stats.total_sales)} accent="#C2593F" testid="stat-sales" />
        <StatCard label="Provvigioni" value={eur(stats.total_commissions)} accent="#15803D" testid="stat-commissions" />
        <StatCard label="In attesa" value={stats.pending_orders ?? 0} accent="#EAB308" testid="stat-pending" />
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        <Dialog open={orderOpen} onOpenChange={setOrderOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-order-btn" className="bg-[#0F4C81] hover:bg-[#0A365C] text-white rounded-full">
              <ShoppingCart className="w-4 h-4 mr-2" /> Nuovo Ordine
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Nuovo Ordine</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3 items-end">
              <div>
                <Label>Cliente *</Label>
                <Select value={client.client_name} onValueChange={(v) => { const c = clients.find((x) => x.name === v); setClient({ ...client, client_name: v, client_type: c?.type || "", client_city: c?.city || "" }); }}>
                  <SelectTrigger className="mt-1" data-testid="order-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.name}>{c.name} — {c.city}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm pb-2 cursor-pointer">
                <Checkbox checked={client.new_point} onCheckedChange={(v) => setClient({ ...client, new_point: !!v })} data-testid="order-newpoint-checkbox" />
                Nuovo punto vendita (+{settings.new_point_bonus ?? 0}% bonus)
              </label>
            </div>
            {clients.length === 0 && <p className="text-xs text-amber-600">Nessun cliente: aggiungine uno dalla scheda "Clienti".</p>}

            <div className="mt-2">
              <Label className="mb-2 block">Aggiungi prodotti (prezzo agente{discount > 0 ? ` −${discount}%` : ""})</Label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1">
                {products.map((p) => (
                  <button key={p.id} onClick={() => addToCart(p)} data-testid={`add-product-${p.id}`}
                          className="text-xs px-3 py-1.5 rounded-full border border-stone-300 hover:bg-[#FEF9C3] transition-colors">
                    + {p.name} <span className="font-mono text-stone-500">{eur(effPrice(p))}</span>
                    {p.category === "fusti" && (p.portions || 0) > 0 ? <span className="ml-1 text-[10px] text-stone-400">· {eur(effPrice(p) / p.portions)}/drink</span> : null}
                  </button>
                ))}
              </div>
            </div>

            {cart.length > 0 && (
              <div className="border border-stone-200 rounded-xl overflow-hidden mt-2">
                <Table>
                  <TableHeader><TableRow><TableHead>Prodotto</TableHead><TableHead className="w-24">Qtà</TableHead><TableHead className="w-28">Prezzo</TableHead><TableHead className="w-24">Totale</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {cart.map((i) => (
                      <TableRow key={i.product_id}>
                        <TableCell className="font-medium">{i.name}</TableCell>
                        <TableCell><Input type="number" min="1" value={i.quantity} onChange={(e) => updateItem(i.product_id, "quantity", e.target.value)} className="h-8" data-testid={`qty-${i.product_id}`} /></TableCell>
                        <TableCell><Input type="number" step="0.01" value={i.unit_price} onChange={(e) => updateItem(i.product_id, "unit_price", e.target.value)} className="h-8" /></TableCell>
                        <TableCell className="font-mono">{eur(i.quantity * i.unit_price)}</TableCell>
                        <TableCell><button onClick={() => removeItem(i.product_id)}><Trash2 className="w-4 h-4 text-red-500" /></button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="flex items-center justify-between bg-[#EFF6FF] rounded-xl px-4 py-3 mt-2">
              <div>
                <div className="text-xs text-stone-500">Totale ordine (netto)</div>
                <div className="font-mono font-bold text-xl text-[#0F4C81]" data-testid="cart-total">{eur(cartTotal)}</div>
                <div className="text-[11px] text-stone-500 mt-0.5">+ IVA 22% {eur(cartVat)} · <b>Tot. {eur(cartGross)}</b></div>
              </div>
              <div className="text-right">
                <div className="text-xs text-stone-500">Provvigione stimata ({effRate.toFixed(1)}%)</div>
                <div className="font-mono font-bold text-xl text-[#15803D]" data-testid="cart-commission">{eur(cartCommission)}</div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submitOrder} data-testid="submit-order-btn" className="bg-[#0F4C81] text-white rounded-full">Invia ordine</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={comOpen} onOpenChange={setComOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-comodato-btn" variant="outline" className="rounded-full border-[#C2593F] text-[#C2593F] hover:bg-[#FFEDD5]">
              <Wrench className="w-4 h-4 mr-2" /> Richiedi Comodato
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Richiesta Comodato Impianto</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Cliente *</Label>
                <Select value={com.client_name} onValueChange={(v) => setCom({ ...com, client_name: v })}>
                  <SelectTrigger className="mt-1" data-testid="comodato-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Macchina *</Label>
                <Select value={com.machine_id} onValueChange={(v) => setCom({ ...com, machine_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="comodato-machine-select"><SelectValue placeholder="Seleziona impianto" /></SelectTrigger>
                  <SelectContent>
                    {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} — {eur(m.cost)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Mesi</Label><Input type="number" value={com.lease_months} onChange={(e) => setCom({ ...com, lease_months: e.target.value })} className="mt-1" /></div>
                <div><Label>Fusti/mese</Label><Input type="number" value={com.monthly_kegs} onChange={(e) => setCom({ ...com, monthly_kegs: e.target.value })} className="mt-1" /></div>
                <div><Label>Margine/fusto</Label><Input type="number" value={com.margin_per_keg} onChange={(e) => setCom({ ...com, margin_per_keg: e.target.value })} className="mt-1" /></div>
              </div>
              {selectedMachine && (
                <div className="bg-[#FFEDD5] rounded-xl p-4 flex items-center gap-3" data-testid="comodato-amortization">
                  <Calculator className="w-8 h-8 text-[#9A3412]" />
                  <div>
                    <div className="text-xs text-stone-600">Assorbimento mensile {eur(comAbsorption)} · Costo impianto {eur(selectedMachine.cost)}</div>
                    <div className="font-semibold text-[#9A3412]">Ammortamento in {comBreakeven ?? "—"} mesi</div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={submitComodato} data-testid="submit-comodato-btn" className="bg-[#C2593F] hover:bg-[#9A3412] text-white rounded-full">Invia richiesta</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Button onClick={() => dl("/reports/commissions.pdf", "provvigioni.pdf")} variant="outline" data-testid="download-commissions-btn" className="rounded-full border-stone-300">
          <FileDown className="w-4 h-4 mr-2" />Estratto provvigioni PDF
        </Button>

        <Button onClick={() => dl("/reports/commissions.xlsx", "provvigioni.xlsx")} variant="outline" data-testid="download-commissions-xlsx-btn" className="rounded-full border-stone-300">
          <FileDown className="w-4 h-4 mr-2" />Estratto Excel
        </Button>

        <Button onClick={() => dl("/reports/listino-agenti.pdf", "listino_agenti.pdf")} variant="outline" data-testid="download-listino-agenti-btn" className="rounded-full border-stone-300">
          <FileDown className="w-4 h-4 mr-2" />Listino Agenti PDF
        </Button>
      </div>

      <Tabs defaultValue="orders">
        <TabsList className="bg-white border border-stone-200 rounded-full p-1 flex-wrap h-auto">
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-full data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white"><Package className="w-4 h-4 mr-2" />Ordini</TabsTrigger>
          <TabsTrigger value="comodati" data-testid="tab-comodati" className="rounded-full data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white"><Wrench className="w-4 h-4 mr-2" />Comodati</TabsTrigger>
          <TabsTrigger value="clienti" data-testid="tab-clienti" className="rounded-full data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white"><Users className="w-4 h-4 mr-2" />Clienti</TabsTrigger>
          <TabsTrigger value="vuoto" data-testid="tab-vuoto" className="rounded-full data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white"><Recycle className="w-4 h-4 mr-2" />Vuoto a Rendere</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead>Articoli</TableHead><TableHead>Totale</TableHead><TableHead>Provvigione</TableHead><TableHead>Stato</TableHead><TableHead>Data</TableHead></TableRow></TableHeader>
              <TableBody>
                {orders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-stone-400 py-8">Nessun ordine ancora</TableCell></TableRow>}
                {orders.map((o) => (
                  <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                    <TableCell className="font-medium">{o.client_name}<div className="text-xs text-stone-400">{o.client_city}</div></TableCell>
                    <TableCell className="text-sm text-stone-500">{o.items.length} art.</TableCell>
                    <TableCell className="font-mono">{eur(o.total)}</TableCell>
                    <TableCell className="font-mono text-[#15803D]">{eur(o.commission_amount)}</TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell className="text-xs text-stone-400">{new Date(o.created_at).toLocaleDateString("it-IT")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="comodati" className="mt-6">
          <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead>Impianto</TableHead><TableHead>Fusti/mese</TableHead><TableHead>Ammortamento</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
              <TableBody>
                {comodati.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-stone-400 py-8">Nessun comodato ancora</TableCell></TableRow>}
                {comodati.map((c) => (
                  <TableRow key={c.id} data-testid={`comodato-row-${c.id}`}>
                    <TableCell className="font-medium">{c.client_name}</TableCell>
                    <TableCell>{c.machine_name}</TableCell>
                    <TableCell className="font-mono">{c.monthly_kegs}</TableCell>
                    <TableCell className="font-mono text-[#9A3412]">{c.breakeven_months ?? "—"} mesi</TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="clienti" className="mt-6"><ClientsPanel /></TabsContent>
        <TabsContent value="vuoto" className="mt-6"><KegsPanel /></TabsContent>
      </Tabs>
    </DashboardShell>
  );
}
